export interface RowSelectionState {
  selectedRowIds: string[]
  anchorId: string | null
}

export interface RowSelectionModifiers {
  shiftKey: boolean
  toggleKey: boolean
}

/**
 * Pure reducer for whole-row selection driven from the row-number cell.
 *
 * Mirrors ``computeColumnSelection`` so both axes behave identically:
 *
 * - `toggleKey` (Ctrl/Cmd+click): add the row if unselected, remove it if selected, leaving the
 *   rest of the selection untouched. The clicked row becomes the new anchor.
 * - `shiftKey` (Shift+click) with an anchor: select the contiguous range of rows between the
 *   anchor and the clicked row (inclusive), preserving the original anchor for re-extension.
 * - otherwise (plain click, Shift+SPACE, or Shift+click with no anchor): select just the clicked
 *   row and make it the anchor.
 *
 * `orderedRowIds` must be the *data* rows in display order (group headers / add-rows excluded,
 * filtered-out rows excluded) so a range spans what the user actually sees.
 */
export const computeRowSelection = (
  orderedRowIds: string[],
  current: RowSelectionState,
  rowId: string,
  modifiers: RowSelectionModifiers
): RowSelectionState => {
  if (modifiers.toggleKey) {
    const selectedRowIds = current.selectedRowIds.includes(rowId)
      ? current.selectedRowIds.filter((id) => id !== rowId)
      : [...current.selectedRowIds, rowId]
    return { selectedRowIds, anchorId: rowId }
  }

  if (modifiers.shiftKey && current.anchorId) {
    const anchorIndex = orderedRowIds.indexOf(current.anchorId)
    const targetIndex = orderedRowIds.indexOf(rowId)
    if (anchorIndex !== -1 && targetIndex !== -1) {
      const [start, end] = anchorIndex <= targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
      return { selectedRowIds: orderedRowIds.slice(start, end + 1), anchorId: current.anchorId }
    }
  }

  return { selectedRowIds: [rowId], anchorId: rowId }
}

/**
 * The moving end of the current row range — the end of the selection that isn't the anchor.
 *
 * A Shift-extended selection is always a contiguous slice with the anchor at one end, so the other
 * end is where the next keyboard step continues from. A single-row selection (and a non-contiguous
 * Ctrl/Cmd+click one, where there is no meaningful "far end") steps from the anchor itself.
 */
export const getRowSelectionFocusId = (orderedRowIds: string[], current: RowSelectionState): string | null => {
  if (!current.anchorId) return null

  const selectedInOrder = orderedRowIds.filter((id) => current.selectedRowIds.includes(id))
  if (selectedInOrder.length === 0) return current.anchorId

  const first = selectedInOrder[0]
  const last = selectedInOrder[selectedInOrder.length - 1]
  // A gappy Ctrl/Cmd+click selection has no meaningful far end, so step from the anchor — the row
  // the user last touched — rather than from whichever selected row happens to sit furthest away.
  const isContiguous = orderedRowIds.indexOf(last) - orderedRowIds.indexOf(first) + 1 === selectedInOrder.length
  if (!isContiguous) return current.anchorId

  return current.anchorId === last ? first : last
}

/**
 * Grow or shrink the row selection by one displayed row — Shift+ArrowUp / Shift+ArrowDown while a
 * whole-row selection is active, which is how Excel extends the row selected by SHIFT+SPACE.
 *
 * Steps the moving end of the range one row in `direction` and re-runs the Shift branch of
 * ``computeRowSelection`` from the preserved anchor, so extending away from the anchor grows the
 * range and stepping back towards it shrinks it. Returns the state unchanged at either end of the
 * grid, or when the selection has no anchor still on screen.
 */
export const extendRowSelection = (
  orderedRowIds: string[],
  current: RowSelectionState,
  direction: 1 | -1
): RowSelectionState => {
  const focusId = getRowSelectionFocusId(orderedRowIds, current)
  if (focusId === null) return current

  const focusIndex = orderedRowIds.indexOf(focusId)
  if (focusIndex === -1) return current

  const nextRowId = orderedRowIds[focusIndex + direction]
  if (nextRowId === undefined) return current

  return computeRowSelection(orderedRowIds, current, nextRowId, { shiftKey: true, toggleKey: false })
}
